/**
 * Cumulative elevation gain for a single coordinate array.
 *
 * Uses a moving-baseline algorithm with a threshold (default 6m) so small
 * GPS/elevation-sample noise doesn't inflate the total: a gain only counts
 * once the climb from the current baseline exceeds the threshold, and the
 * baseline drops on any descent. This is the canonical algorithm used by the
 * route planner (elevationGainString / routesWithStats) and the map
 * measurement tool, so both report the same value for the same route.
 *
 * Coordinates are [lng, lat, elevation?]; the elevation (index 2) is read
 * directly, matching the planner's existing behaviour.
 */
export function elevationGainFromCoordinates(
  coordinates: number[][],
  threshold = 6
): number {
  if (!coordinates || coordinates.length < 2) return 0;

  let totalElevationGain = 0;
  let baseElevation = coordinates[0][2];

  for (let i = 1; i < coordinates.length; i++) {
    const elevation = coordinates[i][2];
    const elevationChange = elevation - baseElevation;

    if (elevationChange > threshold) {
      totalElevationGain += elevationChange;
      baseElevation = elevation; // reset baseline on significant gain
    } else if (elevation < baseElevation) {
      baseElevation = elevation; // reset baseline on any descent
    }
  }

  return totalElevationGain;
}

/**
 * Cumulative elevation loss (descent) for a single coordinate array.
 *
 * The mirror image of {@link elevationGainFromCoordinates}: it runs the exact
 * same moving-baseline 6m-threshold algorithm on negated elevations, so the
 * descent total is computed identically to the ascent total (and both are
 * de-noised the same way). Returns a positive number of metres descended.
 *
 * Coordinates are [lng, lat, elevation?]; the elevation (index 2) is read
 * directly, matching {@link elevationGainFromCoordinates}.
 */
export function elevationLossFromCoordinates(
  coordinates: number[][],
  threshold = 6
): number {
  if (!coordinates || coordinates.length < 2) return 0;

  const negated = coordinates.map((c) => {
    const copy = c.slice();
    copy[2] = -(c[2] ?? 0);
    return copy;
  });

  return elevationGainFromCoordinates(negated, threshold);
}
